import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const LinkTimeEntryModal = ({ isOpen, onClose, note, projects = [], onSuccess }) => {
  const [durationHours, setDurationHours] = useState('');
  const [projectId, setProjectId] = useState(note?.project_id || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (note && isOpen) {
      setProjectId(note.project_id || '');
    }
  }, [note, isOpen]);

  const handleSubmit = async () => {
    if (!durationHours || parseFloat(durationHours) <= 0) {
      toast.error('Please enter a valid duration');
      return;
    }

    const durationMinutes = Math.round(parseFloat(durationHours) * 60);
    if (durationMinutes <= 0) {
      toast.error('Duration must be greater than 0');
      return;
    }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${API_URL}/api`;
      const token = localStorage.getItem('token');

      const payload = {
        duration_minutes: durationMinutes
      };
      
      // Only include project_id if it's not empty
      if (projectId && projectId.trim() !== '') {
        payload.project_id = projectId;
      }

      await axios.post(
        `${API}/notes/${note.id}/link-time-entry`,
        payload,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success('Time entry linked to note!');
      if (onSuccess) {
        await onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error linking time entry:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to link time entry';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !note) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Container - Centered */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal - Centered */}
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Link Time Entry
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0.25"
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  placeholder="1.5"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the duration in hours (e.g., 1.5 for 1 hour 30 minutes)
                </p>
              </div>

              {projects.length > 0 && (
                <div>
                  <Label>Project (optional)</Label>
                  <select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">No project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-sm text-blue-800">
                  💡 This will create a time entry linked to your note "{note.title}" using the same date and time as the note.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:flex sm:flex-row-reverse gap-2 border-t border-gray-200">
            <Button
              onClick={handleSubmit}
              disabled={loading || !durationHours}
              className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              {loading ? 'Linking...' : 'Link Time Entry'}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full sm:w-auto"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkTimeEntryModal;

